import { useEffect, useRef } from 'react';

/**
 * Restaure / persiste la position de scroll pour une clé donnée
 * (par défaut, le pathname de l'URL).
 *
 * @param {Object} options
 * @param {string} [options.storageKey] - Clé optionnelle pour identifier la page/section.
 * @param {boolean} [options.ready=true] - Quand false, la restauration de scroll est retardée (utile quand la page est encore en chargement).
 */
export function useScrollRestoration({ storageKey, ready = true } = {}) {
  const hasRestoredRef = useRef(false);

  const getKey = () => {
    if (typeof window === 'undefined') return 'default';
    return storageKey || window.location.pathname;
  };

  useEffect(() => {
    if (!ready || hasRestoredRef.current) return;
    if (typeof window === 'undefined' || typeof sessionStorage === 'undefined') {
      return;
    }

    // Si un hash est présent, on laisse le navigateur gérer l'ancre
    if (window.location.hash) {
      hasRestoredRef.current = true;
      return;
    }

    const storageKeyFull = `scroll:${getKey()}`;
    const saved = sessionStorage.getItem(storageKeyFull);
    if (saved != null) {
      const y = parseInt(saved, 10);
      if (!Number.isNaN(y) && y > 0) {
        // Attendre que la page ait assez de hauteur (contenu async) avant de restaurer le scroll
        const minHeight = y + (typeof window.innerHeight === 'number' ? window.innerHeight : 400);
        let attempts = 0;
        const maxAttempts = 60; // ~3 s à 50 ms
        const tryScroll = () => {
          attempts += 1;
          const h = Math.max(
            document.body.scrollHeight,
            document.body.offsetHeight,
            document.documentElement.scrollHeight,
            document.documentElement.offsetHeight
          );
          if (h >= minHeight || attempts >= maxAttempts) {
            window.scrollTo(0, y);
            hasRestoredRef.current = true;
            return;
          }
          setTimeout(tryScroll, 50);
        };
        tryScroll();
        return;
      }
    }
    hasRestoredRef.current = true;
  }, [ready, storageKey]);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof sessionStorage === 'undefined') {
      return;
    }

    const storageKeyFull = `scroll:${getKey()}`;

    const handleScroll = () => {
      try {
        sessionStorage.setItem(storageKeyFull, String(window.scrollY || window.pageYOffset || 0));
      } catch (e) {
        // console.error('Error:', e);
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [storageKey]);
}
